import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { fontSize } from "../theme/fontSize";
import { fontWeight } from "../theme/fontWeight";
import { FontFamily } from "../theme/fontFamily";
import { ThemeColor } from "../theme/color";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { GetUserChatAction } from "../../Redux/action/SettingAction";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import { RemovePersonToGroupChatService } from "../../Redux/service/SettingService";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: 520, md: 560 },
  maxWidth: "100%",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 24px 60px rgba(16, 24, 40, 0.22)",
  outline: "none",
};

/** Roles that may manage members of any chat - mirrors ChatController. */
const MANAGER_ROLES = ["admin", "sub_admin"];

const idOf = (value) => String(value?._id || value || "");

const fullName = (person) =>
  [person?.firstName || person?.title, person?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim() || person?.email || "Unknown user";

const initialsOf = (person) =>
  fullName(person)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

/** "Agent · MapleBridge International" - whatever of the two we actually have. */
const subtitleOf = (person) =>
  [person?.roleType, person?.organization?.organizationName]
    .filter(Boolean)
    .join(" · ");

/**
 * Chat members, with a remove control next to each one.
 *
 * The previous version asked the user to find people in two autocomplete boxes
 * and then submit - so removing somebody meant typing their email even though
 * they were already sitting in the chat. This just lists who is in the chat and
 * puts the action where the person is.
 *
 * Who may remove is decided by the same rule as the server
 * (ChatController.removeParticipantsFromChat): the chat creator, or an
 * admin/sub-admin. The creator can never be removed, and removing yourself is
 * "Leave chat" in the conversation menu, not this dialog.
 */
export const RemovePersonToChatChatModal = ({
  RemovePersonChat,
  handleClose,
  chatId,
  Participantlist,
  createdBy,
}) => {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [pendingId, setPendingId] = useState(null); // row awaiting confirmation
  const [removingId, setRemovingId] = useState(null); // row with a request in flight

  const currentUserId = window.sessionStorage.getItem("user_id");
  const currentRoleType = window.sessionStorage.getItem("user_role_type");
  const creatorId = idOf(createdBy);

  const members = useMemo(() => Participantlist || [], [Participantlist]);

  const canManage =
    (creatorId && creatorId === String(currentUserId)) ||
    MANAGER_ROLES.includes(currentRoleType);

  const visibleMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const sorted = [...members].sort((a, b) =>
      fullName(a).localeCompare(fullName(b))
    );
    if (!term) return sorted;
    return sorted.filter((person) =>
      [fullName(person), person?.email, person?.roleType, person?.organization?.organizationName]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    );
  }, [members, search]);

  const close = () => {
    setSearch("");
    setPendingId(null);
    handleClose();
  };

  const removeMember = async (person) => {
    setRemovingId(idOf(person));
    try {
      const res = await RemovePersonToGroupChatService(chatId, {
        participantsToRemove: [idOf(person)],
      });

      if (res.status === 200) {
        dispatch(GetUserChatAction());
        dispatch(
          showSnackbar(`${fullName(person)} removed from the chat`, "success")
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar(
          error?.response?.data?.message || "Could not remove this member",
          "error"
        )
      );
    } finally {
      setRemovingId(null);
      setPendingId(null);
    }
  };

  /** null when removable, otherwise the reason to show instead of the button. */
  const blockedReason = (person) => {
    if (idOf(person) === String(currentUserId)) return "You";
    if (creatorId && idOf(person) === creatorId) return "Chat creator";
    if (!canManage) return null;
    return null;
  };

  return (
    <Modal
      open={RemovePersonChat}
      onClose={close}
      aria-labelledby="chat-members-title"
    >
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            px: 3,
            pt: 2.5,
            pb: 2,
          }}
        >
          <Box>
            <Typography
              id="chat-members-title"
              fontFamily={FontFamily.Poppins}
              fontWeight={fontWeight.W600}
              fontSize={fontSize.h3.large}
              color={ThemeColor.BLACK}
            >
              Chat Members
            </Typography>
            <Typography sx={{ color: "#667085", fontSize: 13, mt: 0.25 }}>
              {members.length} {members.length === 1 ? "member" : "members"}
              {canManage ? " · remove anyone from the list below" : ""}
            </Typography>
          </Box>
          <IconButton size="small" onClick={close} sx={{ color: "#667085" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {members.length > 6 && (
          <Box sx={{ px: 3, pb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search members…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#98A2B3" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "10px" },
              }}
            />
          </Box>
        )}

        {!canManage && (
          <Box
            sx={{
              mx: 3,
              mb: 2,
              px: 1.5,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderRadius: "10px",
              backgroundColor: "#FFF7ED",
              border: "1px solid #FED7AA",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: "#C2410C" }} />
            <Typography sx={{ fontSize: 12.5, color: "#9A3412" }}>
              Only the person who created this chat, or an admin, can remove
              members.
            </Typography>
          </Box>
        )}

        <Divider />

        {/* Members */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1 }}>
          {visibleMembers.length === 0 ? (
            <Typography
              sx={{ textAlign: "center", color: "#98A2B3", fontSize: 13, py: 4 }}
            >
              {search ? "No member matches that search" : "No members in this chat"}
            </Typography>
          ) : (
            <List disablePadding>
              {visibleMembers.map((person) => {
                const personId = idOf(person);
                const reason = blockedReason(person);
                const isPending = pendingId === personId;
                const isRemoving = removingId === personId;
                const isCreator = creatorId && personId === creatorId;

                return (
                  <ListItem
                    key={personId}
                    disableGutters
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: "10px",
                      "&:hover": { backgroundColor: "#F9FAFB" },
                    }}
                    secondaryAction={
                      isRemoving ? (
                        <CircularProgress size={18} />
                      ) : isPending ? (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={() => removeMember(person)}
                            sx={{
                              textTransform: "none",
                              backgroundColor: ThemeColor.RED,
                              fontSize: 12,
                              px: 1.5,
                              "&:hover": { backgroundColor: "#C42127" },
                            }}
                          >
                            Remove
                          </Button>
                          <Button
                            size="small"
                            onClick={() => setPendingId(null)}
                            sx={{ textTransform: "none", fontSize: 12, color: "#667085" }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      ) : reason ? (
                        <Chip
                          label={reason}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#475467",
                            backgroundColor: "#F2F4F7",
                          }}
                        />
                      ) : canManage ? (
                        <Tooltip title={`Remove ${fullName(person)}`} arrow>
                          <IconButton
                            size="small"
                            onClick={() => setPendingId(personId)}
                            sx={{
                              color: "#98A2B3",
                              "&:hover": {
                                color: ThemeColor.RED,
                                backgroundColor: "rgba(232,54,61,0.08)",
                              },
                            }}
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  >
                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        mr: 1.5,
                        fontSize: 14,
                        fontWeight: 600,
                        bgcolor: isCreator ? ThemeColor.BLUE : "#E4E7EC",
                        color: isCreator ? "#FFFFFF" : "#475467",
                      }}
                      src={person?.photo || undefined}
                    >
                      {initialsOf(person)}
                    </Avatar>

                    <Box sx={{ minWidth: 0, pr: 10 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#101828",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fullName(person)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#667085",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {person?.email}
                      </Typography>
                      {subtitleOf(person) && (
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: "#98A2B3",
                            textTransform: "capitalize",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {subtitleOf(person)}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Divider />

        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={close}
            sx={{ textTransform: "none", color: "#475467", fontWeight: 600 }}
          >
            Done
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
